<script setup lang="ts">
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { MapComponent } from '@apps/main/new-desktop/modules/maps';

import { AppIconButton } from '@libs/ui';
import { MapControl } from '@libs/maps';

interface TripOverviewMapProps {
  shouldHaveBorderRadius: boolean;
}

const props = defineProps<TripOverviewMapProps>();

interface TripOverviewMapEmits {
  (event: 'close'): void;
  (event: 'zoom'): void;
}

const emit = defineEmits<TripOverviewMapEmits>();

const mapStore = useMapStore();
</script>

<template>
  <div
    class="trip-overview-map"
    :class="{
      'with-border': props.shouldHaveBorderRadius,
      'full-screen': mapStore.shouldShowFullScreenMap,
      summary: !mapStore.shouldShowFullScreenMap,
    }"
  >
    <div
      class="trip-overview-map-wrapper"
      :class="{
        loading: !mapStore.isMapCreated,
      }"
    >
      <MapComponent
        :key="mapStore.mapTypeId"
        :marker-formatters="mapStore.mapProperties.markerFormatters"
        :markers="mapStore.mapProperties.markers"
        :route="mapStore.mapProperties.route"
        :show-all-markers="mapStore.mapProperties.showAllMarkers"
        :start-rectangle="mapStore.mapProperties.startRectangle"
        :gesture-handling="mapStore.mapProperties.gestureHandling"
        :locale="mapStore.mapProperties.locale"
        :map-controls="
          mapStore.shouldShowFullScreenMap
            ? [MapControl.zoomControl, MapControl.mapTypeControl]
            : [MapControl.mapTypeControl]
        "
        @on-view-changed="mapStore.mapViewChanged"
        @map-created="mapStore.setDynamicMapIntegration"
        @on-map-type-id-changed="mapStore.setMapTypeId"
      />
      <AppIconButton
        v-if="mapStore.shouldShowFullScreenMap"
        class="close-button"
        type="secondary"
        state="enabled"
        size="medium"
        :ariaLabel="$t('a11y.close_image_gallery')"
        :icon="{
          name: 'close',
        }"
        @click="emit('close')"
      />

      <AppIconButton
        v-if="!mapStore.shouldShowFullScreenMap"
        class="zoom-button"
        type="secondary"
        state="enabled"
        size="medium"
        :ariaLabel="$t('a11y.open_image_gallery')"
        :icon="{
          name: 'zoom',
        }"
        @click="emit('zoom')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.trip-overview-map {
  &.summary {
    .trip-overview-map-wrapper {
      width: 100%;
      height: 100%;
      align-self: center;
    }
  }

  &.with-border {
    .trip-overview-map-wrapper {
      border-radius: var(--border-radius-8);
    }
  }

  &.full-screen {
    --map-spacing: var(--spacing-24);

    background-color: rgba(0, 0, 0, 0.9);
    width: 100vw;
    height: 100vh;
    height: 100svh;
    position: fixed;
    z-index: var(--modal-z-index);
    @include for-more-or-equal-than-1200-px {
      padding: var(--map-spacing) 0;
    }

    .trip-overview-map-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      @include for-more-or-equal-than-1200-px {
        width: 70%;
        min-width: 960px;
        max-height: calc(100vh - var(--map-spacing) * 2);
        aspect-ratio: 7/5;
        border-radius: var(--border-radius-8);
        overflow: hidden;
        margin: 0 auto;
      }
    }
  }

  .trip-overview-map-wrapper {
    overflow: hidden;
    position: relative;

    &.loading::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;

      @include animated-background-secondary;
    }

    &::after {
      content: none;
    }

    .close-button {
      position: absolute;
      top: var(--spacing-24);
      right: var(--spacing-24);

      @include for-less-or-equal-than-599-px {
        top: var(--spacing-16);
        right: var(--spacing-16);
      }
    }
    .zoom-button {
      position: absolute;
      top: var(--spacing-12);
      right: var(--spacing-12);
    }
  }
}

.trip-summary-location-marker,
.trip-summary-accommodation-marker {
  cursor: pointer;
}
</style>

<style lang="scss">
.trip-overview-map {
  @include map-zoom-control(34px, 40px, -30px, -75px);
  &.summary {
    @include map-type-switch(var(--spacing-12));
  }

  &.full-screen {
    @include map-type-switch(var(--spacing-24));
  }
}
</style>
